import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TooltipTriggerModal from '../../../components/display/tooltip-trigger-modal';

const SectionHeader = ({ index, title }: { index: number; title: string }) => {
  const theme = useTheme();
  const circleBg = theme.palette.primary?.main ?? '#1976d2';
  return (
    <Box display="flex" alignItems="center" mt={2} mb={1.5}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: circleBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: 700,
          mr: 1.5,
        }}
      >
        {index}
      </Box>
      <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
    </Box>
  );
};

export default function SecureLinkAutomationInfoTooltip(): JSX.Element {
  const content = (
    <Box sx={{ p: 1 }}>
      <SectionHeader
        index={1}
        title="Patient must be opted into automated Secure Link 'SMS and Automated Secure Link' option in demographics."
      />
      <Typography sx={{ ml: 5 }}>
        User selects &quot;anchor drug&quot; to base send schedule around (patient can only have one
        anchor drug).
      </Typography>
      <Typography sx={{ fontWeight: 600, mt: 1, ml: 5 }}>
        Eligibility Criteria for Anchor Drug:
      </Typography>
      <Box component="ul" sx={{ mt: 0.5, pl: 6 }}>
        <li>
          Must be SP, L, CDM, or Ancillary (cannot be SP-PA or have Clinic Administered designation)
          <Box component="ul" sx={{ mt: 0.5, pl: 3 }}>
            <li>If SP, L, CDM = can be PRN</li>
            <li>If Ancillary, cannot be PRN</li>
          </Box>
        </li>
        <li>Administration status must be On-Therapy or Pre-Therapy</li>
      </Box>

      <Divider sx={{ my: 2 }} />

      <SectionHeader index={2} title="Secure Link will automatically send following these rules:" />
      <Box component="ol" sx={{ mt: 0.5, pl: 6, listStyleType: 'decimal' }}>
        <li>Follow-up date = today for anchor medication</li>
        <li>Fill Cycle must be &gt; 1</li>
        <li>Will only send automatically up to twice per fill cycle</li>
        <li>Will send at 9AM local time</li>
      </Box>

      <Divider sx={{ my: 2 }} />

      <SectionHeader
        index={3}
        title="Other patient medications will automatically be included in the bundle with the anchor med following these rules:"
      />
      <Box component="ul" sx={{ mt: 0.5, pl: 6 }}>
        <li>F/U date overdue or within 3 days of F/u date of anchor medication FC</li>
        <li>Med is not SP-PA or designated as clinic administered</li>
        <li>If med is targeted, administration status must be on-therapy.</li>
        <li>If med is ancillary, administration status can be on-therapy or pre-therapy</li>
      </Box>

      <Divider sx={{ my: 2 }} />

      <SectionHeader
        index={4}
        title="The following exceptions will prevent a link from sending automatically:"
      />
      <Box component="ul" sx={{ mt: 0.5, pl: 6 }}>
        <li>
          PA expiration date for any medication that would otherwise be included is &lt; 10d from
          today OR there is an open NEW (i.e., not renewal) PA task for any medication that would
          otherwise be included
        </li>
        <li>
          CSL task follow up date for any medication that would otherwise be included is &lt;= 30d
          from today
        </li>
        <li>
          The FC status for any medication that would otherwise be included is anything other than
          &quot;Required&quot;
        </li>
        <li>
          There is an open FA task with F/U date &lt;= 30d from today for any medication that would
          otherwise be included
        </li>
        <li>
          There is an open INT task for any medication that would otherwise be included with F/U
          date &lt;= 30d from today
        </li>
        <li>
          There is an FC task with a follow-up date 4-14d away that would otherwise qualify to be
          included
        </li>
      </Box>
    </Box>
  );

  return (
    <TooltipTriggerModal
      tooltipText="Secure Link Automation Info"
      modalTitle="Secure Link Automation Info"
    >
      {content}
    </TooltipTriggerModal>
  );
}
